import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import type { AxiosError } from "axios";
import type { InsightRequestState } from "../types/insightApi";

export interface UseInsightAsyncResult<T> {
	status: InsightRequestState;
	data: T | null;
	errorMessage: string | null;
	/** Re-runs the fetcher with a fresh AbortController, e.g. for a "Retry" button. */
	refetch: () => void;
}

export interface ClassifiedError {
	status: InsightRequestState;
	message: string;
}

export function classifyError(error: unknown): ClassifiedError {
	const axiosError = error as AxiosError<{ message?: string }>;
	const httpStatus = axiosError?.response?.status;
	const message = axiosError?.response?.data?.message || axiosError?.message || "Unexpected error";
	if (httpStatus === 401 || httpStatus === 403) return { status: "unauthorized", message };
	if (httpStatus === 404) return { status: "not-found", message };
	if (httpStatus === 503 || httpStatus === 504) return { status: "service-down", message };
	return { status: "error", message };
}

/**
 * Generic cancellable, stale-response-safe fetch hook shared by every
 * Insight V2 surface (source list, scopes, topology, findings).
 *
 * - Cancellation: aborts the in-flight request (via `AbortController`) when
 *   `deps` change again before it resolves, or on unmount.
 * - Stale-response protection: a monotonic request sequence number ignores
 *   a response that arrives after a newer request has already started, even
 *   if the older request wasn't (or couldn't be) aborted in time.
 * - `fetcher: null` short-circuits to `status: "idle"` — use this while a
 *   prerequisite selection (e.g. no source chosen yet) is missing, instead
 *   of firing a request that's guaranteed to fail.
 * - Stale-*data* protection: `data` is cleared the instant a new request
 *   starts (identity change or manual `refetch()`) and again if that request
 *   fails. A caller that gates a "showing partial data" banner on `data`
 *   being present can therefore never combine a previous source's success
 *   payload with a new source's loading/error/unauthorized/not-found state —
 *   see the CTX-97 "stale partial banner and hidden cluster-member context"
 *   defect this fixes.
 *
 * HTTP status codes are mapped to the `InsightRequestState` the backend
 * contract implies (401/403 -> unauthorized, 404 -> not-found used both for
 * "doesn't exist" and "not yours" per the tenant-isolation contract,
 * 503/504 -> service-down), so callers can render a distinct banner per
 * failure mode instead of one generic error message.
 */
export function useInsightAsync<T>(
	fetcher: ((signal: AbortSignal) => Promise<T>) | null,
	deps: DependencyList,
	options?: { isEmpty?: (data: T) => boolean }
): UseInsightAsyncResult<T> {
	const [status, setStatus] = useState<InsightRequestState>(fetcher ? "loading" : "idle");
	const [data, setData] = useState<T | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const requestSeqRef = useRef(0);
	const abortRef = useRef<AbortController | null>(null);
	const isEmptyRef = useRef(options?.isEmpty);
	isEmptyRef.current = options?.isEmpty;

	const run = useCallback(() => {
		abortRef.current?.abort();

		if (!fetcher) {
			requestSeqRef.current += 1;
			setStatus("idle");
			setData(null);
			setErrorMessage(null);
			return;
		}

		const controller = new AbortController();
		abortRef.current = controller;
		const seq = (requestSeqRef.current += 1);

		setStatus("loading");
		setErrorMessage(null);
		// Clear immediately, not just on failure: a new request's identity
		// (different source/scope) must never render alongside the previous
		// request's data while the new one is in flight.
		setData(null);

		fetcher(controller.signal)
			.then((result) => {
				if (seq !== requestSeqRef.current) return;
				const empty = isEmptyRef.current ? isEmptyRef.current(result) : false;
				setData(result);
				setStatus(empty ? "empty" : "success");
			})
			.catch((error) => {
				if (controller.signal.aborted || seq !== requestSeqRef.current) return;
				const classified = classifyError(error);
				setStatus(classified.status);
				setErrorMessage(classified.message);
				setData(null);
			});
		// `fetcher` is intentionally omitted: callers pass a fresh closure each
		// render, so depending on it would defeat memoization — `deps` is the
		// caller-declared identity for "should this re-run".
	}, deps);

	useEffect(() => {
		run();
		return () => {
			abortRef.current?.abort();
		};
	}, [run]);

	return { status, data, errorMessage, refetch: run };
}
